<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="760px">
      <v-card class="vp-dialog-card">
        <v-card-title class="vp-dialog-title">
          <span class="vp-dialog-title__text">
            {{
              $t(
                "wkt.admin.vacation.policy.update-vacation-accrual-dialog.title",
              )
            }}
          </span>
        </v-card-title>

        <v-card-text class="vp-dialog-body">
          <div class="vp-dialog-description">
            {{
              $t("wkt.admin.vacation-policy.update-accrual-dialog.description")
            }}
          </div>

          <v-form ref="form" class="vp-form">
            <v-row>
              <v-col cols="12" md="5">
                <v-menu
                  ref="dateMenu"
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="dateFormatted"
                      :label="
                        $t(
                          'wkt.admin.vacation-policy.general.accrual_date.current',
                        )
                      "
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>

                  <div class="vp-date-picker-wrapper">
                    <v-date-picker
                      v-model="selectedDate"
                      :active-picker.sync="activePicker"
                      no-title
                      :locale="$i18n.locale"
                      :min="minDate"
                      :max="maxDate"
                      :first-day-of-week="1"
                      @input="onDateSelected"
                    />
                  </div>
                </v-menu>
              </v-col>
            </v-row>

            <v-text-field
              v-model="confirmationText"
              dense
              class="vp-confirm-field"
              :label="confirmationLabel"
              :rules="[confirmationRule]"
            />
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="vp-dialog-actions">
          <v-spacer />
          <v-btn
            v-if="canModify && isCorporate"
            color="#185EA9"
            text
            class="vp-dialog-button"
            @click="ok"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn text class="vp-dialog-button" @click="showDialog = false">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>

        <LoadingDialog :loading="loading" />
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import api from "../../../api/worktime";
import BaseModule from "../../common/BaseModule";
import LoadingDialog from "../../common/LoadingDialog";
import deepClone from "../../util/deep-clone";

export default {
  name: "VacationPolicyAccrualDateDialog",
  mixins: [BaseModule],
  components: {
    LoadingDialog,
  },
  data() {
    return {
      showDialog: false,
      loading: false,
      canModify: false,
      isCorporate: false,
      company: null,
      callback: null,
      confirmationText: "",
      selectedDate: null,
      dateFormatted: "",
      dateMenu: false,
      activePicker: "DATE",
    };
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
    minDate() {
      return `${this.currentYear}-01-01`;
    },
    maxDate() {
      return `${this.currentYear}-12-31`;
    },
    confirmationToken() {
      return this.$t(
        "wkt.admin.vacation-policy.vacation_accrual_date-dialog.text-to-validate",
      );
    },
    confirmationLabel() {
      return String(
        this.$t("wkt.admin.vacation-policy.update-balance-dialog.establish"),
      ).replace("%s", this.confirmationToken);
    },
  },
  methods: {
    emitError(message) {
      if (this.callback) {
        this.callback({
          success: false,
          message,
        });
      }
    },
    open(company, canModify, isCorporate, callback) {
      this.company = company ? deepClone(company) : null;
      this.canModify = canModify;
      this.isCorporate = isCorporate;
      this.callback = callback;
      this.confirmationText = "";
      this.selectedDate = this.normalizeDate(
        this.company ? this.company.vacationDateLastInit : null,
      );
      this.dateFormatted = this.formatDateForInput(this.selectedDate);
      this.dateMenu = false;
      this.activePicker = "DATE";

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.showDialog = true;
    },
    normalizeDate(simpleDate) {
      const day = simpleDate && simpleDate.day ? simpleDate.day : 1;
      const month = simpleDate && simpleDate.month ? simpleDate.month : 1;

      return `${this.currentYear}-${String(month).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;
    },
    formatDateForInput(value) {
      return value ? this.formatISOStringDateToLocalDate(value) : "";
    },
    confirmationRule(value) {
      return (
        value === this.confirmationToken ||
        this.$t("wkt.admin.vacation-policy.vacation_accrual_date-dialog.error")
      );
    },
    onDateSelected(value) {
      this.selectedDate = value;
      this.dateFormatted = this.formatDateForInput(value);
      this.dateMenu = false;
      this.activePicker = "DATE";
    },
    ok() {
      if (!this.canModify || !this.isCorporate) {
        this.showDialog = false;
        return;
      }

      if (!this.$refs.form.validate()) {
        return;
      }

      const [year, month, day] = String(this.selectedDate || this.minDate)
        .split("-")
        .map(Number);
      const updatedCompany = deepClone(this.company);

      updatedCompany.vacationDateLastInit = {
        year,
        month,
        day,
      };

      this.loading = true;

      api.updateMyCompany(
        updatedCompany,
        (response) => {
          this.loading = false;

          if (!response || !response.rsOK) {
            this.emitError(
              response
                ? response.message
                : this.$t("wkt.admin.common.error.save_server"),
            );
            return;
          }

          this.showDialog = false;

          if (this.callback) {
            this.callback({
              success: true,
              company: updatedCompany,
              message: response.message,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.emitError(this.getCallbackErrorMessage(
            error,
            this.$t("wkt.admin.common.error.save_server"),
          ));
        },
      );
    },
  },
  watch: {
    dateMenu(value) {
      if (value) {
        this.$nextTick(() => {
          this.activePicker = "DATE";
        });
      }
    },
    activePicker(value) {
      if (value === "YEAR") {
        this.$nextTick(() => {
          this.activePicker = "DATE";
        });
      }
    },
  },
};
</script>

<style scoped>
.vp-dialog-card {
  border-radius: 6px;
}

.vp-dialog-title {
  padding: 18px 18px 8px;
}

.vp-dialog-title__text {
  color: #1f2833;
  font-size: 22px;
  font-weight: 500;
}

.vp-dialog-body {
  padding: 8px 18px 12px;
}

.vp-dialog-description {
  margin-bottom: 16px;
  color: #4f5966;
  font-size: 13px;
  line-height: 1.55;
}

.vp-form {
  padding-top: 4px;
}

.vp-date-picker-wrapper {
  border: 1px solid #e6eaf0;
  border-radius: 6px;
  overflow: hidden;
}

.vp-confirm-field {
  margin-top: 18px;
}

.vp-dialog-actions {
  padding: 12px 18px;
}

.vp-dialog-button {
  font-size: 0.9em;
  font-weight: 400;
}

.vp-dialog-card /deep/ .v-input__slot::before {
  border-color: #d8dde6 !important;
}

.vp-dialog-card /deep/ .v-label {
  font-size: 12px;
}

.vp-dialog-card /deep/ .v-date-picker-header__value button:nth-child(2) {
  display: none;
}
</style>
